import React, { useEffect, useMemo, useState } from "react";
import { Calculator, TrendingUp, ArrowRight } from "lucide-react";
import { formatAmount, pickAmount } from "../lib/currency";

const fmt = (amount, currency = "INR") => formatAmount(amount, currency);

// Per-currency defaults and rates (spec: framework v June 2026).
const DEFAULTS_BY_CCY = {
    INR: { spend: 50000, hourly: 400 },
    USD: { spend: 600,   hourly: 8 },
    EUR: { spend: 552,   hourly: 7.4 },   // 600 USD × 0.92 / 8 × 0.92
};

// Hours saved per user per month — used in productivity calculation.
const HOURS_SAVED_PER_USER = 2;

// Matched Scitus plan based on team size. Annual prices (per spec).
const MATCH_PLAN = (teamSize) => {
    if (teamSize <= 10) {
        return {
            name: "Mativex Annual",
            description: "Single product — best fit for small ops teams",
            monthly: { INR: 9600, USD: 119 },
        };
    }
    if (teamSize <= 50) {
        return {
            name: "Retail Suite Annual",
            description: "4-product bundle covering BI, billing, procurement, signing",
            monthly: { INR: 24000, USD: 288 },
        };
    }
    if (teamSize <= 150) {
        return {
            name: "Real Estate Suite Annual",
            description: "Operations-heavy 4-product bundle",
            monthly: { INR: 32400, USD: 389 },
        };
    }
    return {
        name: "Enterprise — talk to us",
        description: "Custom-quoted full platform engagement",
        monthly: { INR: 60000, USD: 720 },
    };
};

const computeRoi = ({ currentSpend, teamSize, currency }) => {
    const matched = MATCH_PLAN(teamSize);
    const scitusCost = pickAmount(matched.monthly, currency);
    const hourly = DEFAULTS_BY_CCY[currency]?.hourly || DEFAULTS_BY_CCY.INR.hourly;

    const toolingSavings = Math.max(0, currentSpend - scitusCost);
    const productivitySavings = teamSize * HOURS_SAVED_PER_USER * hourly;
    const totalMonthly = toolingSavings + productivitySavings;
    const annual = totalMonthly * 12;
    const roiPct = scitusCost > 0 ? (totalMonthly / scitusCost) * 100 : 0;
    return {
        scitusCost,
        matched,
        toolingSavings,
        productivitySavings,
        totalMonthly,
        annual,
        roiPct,
        hourly,
    };
};

export const RoiCalculator = ({ currency = "INR" }) => {
    const [teamSize, setTeamSize] = useState(25);
    const [currentSpend, setCurrentSpend] = useState(
        () => DEFAULTS_BY_CCY[currency]?.spend ?? 50000
    );
    const [userTouchedSpend, setUserTouchedSpend] = useState(false);

    // When currency switches and user hasn't manually changed the spend,
    // reset to that currency's default so the slider doesn't show a stale
    // number from another currency's scale.
    useEffect(() => {
        if (userTouchedSpend) return;
        const next = DEFAULTS_BY_CCY[currency]?.spend;
        if (next !== undefined) setCurrentSpend(next);
    }, [currency, userTouchedSpend]);

    const r = useMemo(
        () => computeRoi({
            teamSize: Number(teamSize),
            currentSpend: Number(currentSpend),
            currency,
        }),
        [teamSize, currentSpend, currency]
    );

    // Spend slider max — sensible per currency. Roughly 4x the default.
    const spendMax = currency === "INR" ? 200000 : currency === "USD" ? 2400 : 2200;

    return (
        <div
            className="card"
            data-testid="roi-calculator"
            style={{
                marginTop: 80,
                padding: 0,
                background:
                    "radial-gradient(circle at 90% 10%, rgba(0,180,216,0.12) 0%, transparent 55%), var(--surface-2)",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 0,
                }}
                className="roi-grid"
            >
                <div style={{ padding: "40px 36px", borderRight: "1px solid var(--border)" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 14,
                        }}
                    >
                        <span
                            className="icon-square"
                            style={{
                                width: 36,
                                height: 36,
                                background: "var(--accent-soft)",
                                color: "var(--accent)",
                                borderRadius: 10,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Calculator size={18} />
                        </span>
                        <span className="eyebrow" style={{ padding: "4px 10px" }}>
                            ROI Calculator
                        </span>
                    </div>
                    <h3
                        className="h2"
                        style={{ fontSize: "1.6rem", margin: "0 0 12px 0" }}
                    >
                        How much could Scitus save you?
                    </h3>
                    <p className="muted" style={{ margin: 0 }}>
                        Slide your team size and current monthly SaaS spend. We&apos;ll show
                        a conservative estimate of what consolidating onto Scitus
                        could return.
                    </p>

                    <div style={{ marginTop: 28 }}>
                        <label
                            htmlFor="roi-team"
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "var(--text-muted)",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                marginBottom: 8,
                            }}
                        >
                            <span>Team size</span>
                            <span style={{ color: "var(--text)" }} data-testid="roi-team-display">
                                {teamSize} users
                            </span>
                        </label>
                        <input
                            id="roi-team"
                            type="range"
                            min="1"
                            max="100"
                            value={teamSize}
                            onChange={(e) => setTeamSize(e.target.value)}
                            className="roi-range"
                            data-testid="roi-team-slider"
                            style={{ "--roi-fill": `${((Number(teamSize) - 1) / 99) * 100}%` }}
                        />
                    </div>

                    <div style={{ marginTop: 22 }}>
                        <label
                            htmlFor="roi-spend"
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "var(--text-muted)",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                marginBottom: 8,
                            }}
                        >
                            <span>Current monthly tool spend</span>
                            <span style={{ color: "var(--text)" }} data-testid="roi-spend-display">
                                {fmt(currentSpend, currency)}
                            </span>
                        </label>
                        <input
                            id="roi-spend"
                            type="range"
                            min="0"
                            max={spendMax}
                            step="500"
                            value={currentSpend}
                            onChange={(e) => { setUserTouchedSpend(true); setCurrentSpend(e.target.value); }}
                            className="roi-range"
                            data-testid="roi-spend-slider"
                            style={{ "--roi-fill": `${(Number(currentSpend) / spendMax) * 100}%` }}
                        />
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "var(--text-dim)",
                                fontSize: "0.75rem",
                                marginTop: 6,
                            }}
                        >
                            <span>{fmt(0, currency)}</span>
                            <span>{fmt(spendMax, currency)}</span>
                        </div>
                    </div>

                    <p
                        className="muted"
                        style={{ marginTop: 24, fontSize: "0.8rem", lineHeight: 1.6 }}
                    >
                        Assumes {HOURS_SAVED_PER_USER} hours of admin work saved per
                        user per month at a fully-loaded rate of{" "}
                        {fmt(r.hourly, currency)}/hour. Tooling savings = your current
                        spend minus your matched Scitus plan (annual pricing). We round
                        down — your real number is usually higher.
                    </p>
                </div>

                <div style={{ padding: "40px 36px" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 18,
                        }}
                    >
                        <TrendingUp size={18} color="var(--accent)" />
                        <span className="eyebrow" style={{ padding: "4px 10px" }}>
                            Your projected savings
                        </span>
                    </div>

                    <div
                        style={{
                            background: "var(--bg)",
                            border: "1px solid var(--border)",
                            borderRadius: 14,
                            padding: 28,
                        }}
                    >
                        <div
                            style={{
                                fontFamily: "'Cabinet Grotesk'",
                                fontSize: "0.78rem",
                                color: "var(--text-muted)",
                                textTransform: "uppercase",
                                letterSpacing: "0.12em",
                            }}
                        >
                            Total monthly savings
                        </div>
                        <div
                            style={{
                                fontFamily: "'Cabinet Grotesk'",
                                fontSize: "clamp(2.2rem, 4vw, 3rem)",
                                fontWeight: 800,
                                color: "var(--accent)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                marginTop: 6,
                            }}
                            data-testid="roi-total-monthly"
                        >
                            {fmt(r.totalMonthly, currency)}
                        </div>
                        <div
                            style={{
                                color: "var(--text-muted)",
                                fontSize: "0.92rem",
                                marginTop: 6,
                            }}
                            data-testid="roi-annual"
                        >
                            ≈ {fmt(r.annual, currency)} per year · {Math.round(r.roiPct)}% ROI vs. plan cost
                        </div>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                            marginTop: 18,
                        }}
                    >
                        <div
                            style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 10,
                                padding: 16,
                            }}
                            data-testid="roi-tooling-savings"
                        >
                            <div
                                style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.74rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                Tooling saved
                            </div>
                            <div
                                style={{
                                    fontFamily: "'Cabinet Grotesk'",
                                    fontWeight: 800,
                                    fontSize: "1.3rem",
                                    marginTop: 4,
                                }}
                            >
                                {fmt(r.toolingSavings, currency)}
                            </div>
                            <div
                                style={{
                                    color: "var(--text-dim)",
                                    fontSize: "0.75rem",
                                    marginTop: 2,
                                }}
                            >
                                / month
                            </div>
                        </div>
                        <div
                            style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 10,
                                padding: 16,
                            }}
                            data-testid="roi-productivity-savings"
                        >
                            <div
                                style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.74rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                Time recovered
                            </div>
                            <div
                                style={{
                                    fontFamily: "'Cabinet Grotesk'",
                                    fontWeight: 800,
                                    fontSize: "1.3rem",
                                    marginTop: 4,
                                }}
                            >
                                {fmt(r.productivitySavings, currency)}
                            </div>
                            <div
                                style={{
                                    color: "var(--text-dim)",
                                    fontSize: "0.75rem",
                                    marginTop: 2,
                                }}
                            >
                                / month
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 22,
                            padding: "16px 18px",
                            background: "var(--surface)",
                            border: "1px solid var(--border)",
                            borderRadius: 10,
                            fontSize: "0.9rem",
                        }}
                    >
                        <span style={{ color: "var(--text-muted)" }}>
                            Matched plan
                        </span>
                        <span
                            style={{
                                color: "var(--text)",
                                fontWeight: 700,
                                fontFamily: "'Cabinet Grotesk'",
                            }}
                            data-testid="roi-matched-plan"
                        >
                            {r.matched.name} · {fmt(r.scitusCost, currency)}/mo
                        </span>
                    </div>

                    <a
                        href="/contact"
                        className="btn btn-primary btn-lg"
                        style={{ width: "100%", marginTop: 22 }}
                        data-testid="roi-book-demo-btn"
                    >
                        See it in your business <ArrowRight size={18} />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default RoiCalculator;
